import EquipmentResourceFields from "@/components/CompanyResource/CreateCompanyResourceModal/EquipmentResourceFields";
import HumanResourceFields from "@/components/CompanyResource/CreateCompanyResourceModal/HumanResourceFields";
import CurrentUserContext from "@/Contexts/CurrentUser";
import {
  CompanyResourceCreate,
  FuelTypeEnum,
  GetResourceCategoriesDocument,
  ResourceMetaStatusTypeEnum,
} from "@/graphql/generated/graphql";
import type { ResourceMetaCreate } from "@/graphql/generated/graphql";
import type { ResourceMetaChangeHandler } from "@/components/CompanyResource/CreateCompanyResourceModal/types";
import { useQuery } from "@apollo/client/react";
import { Box, Grid, Stack, TextField, Typography } from "@mui/material";
import { find } from "lodash";
import { useContext } from "react";

type ImportType = "human" | "equipment" | "clients";

interface SingleResourceFormProps {
  importType: ImportType;
  resourceLabel: string;
  companyResource: CompanyResourceCreate;
  setCompanyResource: React.Dispatch<React.SetStateAction<CompanyResourceCreate>>;
}

const SingleResourceForm: React.FC<SingleResourceFormProps> = ({
  importType,
  resourceLabel,
  companyResource,
  setCompanyResource,
}) => {
  const { currentUser } = useContext(CurrentUserContext);

  const { data: categories } = useQuery(GetResourceCategoriesDocument, {
    skip: importType !== "equipment" || !currentUser?.defaultCompany?.id,
    variables: { companyId: currentUser?.defaultCompany?.id as string },
  });

  const resourceCategories = categories?.getResourceCategories || [];
  const vehicleCategory = find(resourceCategories, category => category.name === "Vehicle");
  const isVehicle = !!vehicleCategory && vehicleCategory.id === companyResource.resourceMeta.resourceCategoryId;

  const handleMetaUpdate: ResourceMetaChangeHandler = (key, value) => {
    const isSelectingVehicle = key === "resourceCategoryId" && vehicleCategory?.id === value;
    const resourceCategoryId = value as ResourceMetaCreate["resourceCategoryId"];

    setCompanyResource((previous) => ({
      ...previous,
      resourceMeta: key === "resourceCategoryId"
        ? {
          resourceCategoryId,
          equipmentType: previous.resourceMeta.equipmentType,
          ...(isSelectingVehicle && { fuelType: FuelTypeEnum.Diesel, status: ResourceMetaStatusTypeEnum.Available }),
        }
        : { ...previous.resourceMeta, [key]: value },
    }));
  };

  return (
    <Box sx={{ height: "100%", pr: { md: 1.5 } }}>
      <Stack spacing={0.75} mb={2.5}>
        <Typography variant="h6" fontWeight={600}>
          Create a single {resourceLabel}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Enter the details below to add a single {resourceLabel}.
        </Typography>
      </Stack>

      <Grid container size={12} p={2}>
        <TextField
          label="Resource Name"
          variant="outlined"
          value={companyResource.name}
          fullWidth
          onChange={(event) => setCompanyResource((previous) => ({ ...previous, name: event.target.value }))}
        />
      </Grid>

      {importType === "human" && (
        <HumanResourceFields resourceMeta={companyResource.resourceMeta} onChange={handleMetaUpdate} />
      )}

      {importType === "equipment" && (
        <EquipmentResourceFields
          isEditing={false}
          resourceMeta={companyResource.resourceMeta}
          onChange={handleMetaUpdate}
          isVehicle={isVehicle}
          resourceCategories={resourceCategories}
        />
      )}
    </Box>
  );
};

export default SingleResourceForm;
